import React, { useState, useEffect } from 'react';
import '../assets/PaymentAdjustmentForm.css';

const PaymentAdjustmentForm = ({ 
  tutors, 
  onSubmit, 
  onCancel, 
  initialData = null,
  isEdit = false 
}) => {
  const [formData, setFormData] = useState({
    tutor: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    amount: '',
    type: 'bonus',
    reason: '',
    isProfitShareAdjustment: false
  });

  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showTutorDropdown, setShowTutorDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Populate form with initial data if editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        tutor: initialData.tutor?._id || initialData.tutor,
        month: initialData.month,
        year: initialData.year,
        amount: initialData.amount.toString(),
        type: initialData.type,
        reason: initialData.reason,
        isProfitShareAdjustment: initialData.isProfitShareAdjustment || false
      });
      
      // Set search term to tutor name if available
      if (initialData.tutor?.name) {
        setSearchTerm(initialData.tutor.name);
      }
    }
  }, [initialData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.tutor-selector')) {
        setShowTutorDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2019 + 6 }, (_, i) => 2019 + i);

  const adjustmentTypes = [
    { value: 'bonus', label: 'Bonus' },
    { value: 'deduction', label: 'Deduction' },
    { value: 'correction', label: 'Correction' },
    { value: 'one-time', label: 'One-time Payment' },
    { value: 'custom', label: 'Custom' }
  ];

  const getFilteredTutors = () => {
    if (!searchTerm) return tutors;
    
    return tutors.filter(t => 
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleTutorSelect = (tutor) => {
    setFormData({ ...formData, tutor: tutor._id });
    setSearchTerm(tutor.name);
    setShowTutorDropdown(false);
    setErrors({ ...errors, tutor: '' });
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tutor) {
      newErrors.tutor = 'Please select a tutor';
    }

    if (!formData.amount || formData.amount === '0') {
      newErrors.amount = 'Amount cannot be zero';
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount)) {
      newErrors.amount = 'Please enter a valid number';
    }

    if (!formData.reason || formData.reason.trim().length === 0) {
      newErrors.reason = 'Reason is required';
    }

    if (!formData.month || formData.month < 1 || formData.month > 12) {
      newErrors.month = 'Please select a valid month';
    }

    if (!formData.year) {
      newErrors.year = 'Please select a year';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        tutor: formData.tutor,
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        amount: parseFloat(formData.amount),
        type: formData.type,
        reason: formData.reason.trim(),
        isProfitShareAdjustment: formData.isProfitShareAdjustment
      };

      await onSubmit(submitData);

      // Reset form if not editing
      if (!isEdit) {
        setFormData({
          tutor: '',
          month: new Date().getMonth() + 1,
          year: new Date().getFullYear(),
          amount: '',
          type: 'bonus',
          reason: '',
          isProfitShareAdjustment: false
        });
        setSearchTerm('');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="payment-adjustment-form">
      <h3>{isEdit ? 'Edit Payment Adjustment' : 'Add Payment Adjustment'}</h3>
      
      <form onSubmit={handleSubmit}>
        {/* Tutor Selector */}
        <div className="form-group">
          <label>Tutor *</label>
          <div className="tutor-selector">
            <input
              type="text"
              className={`tutor-search ${errors.tutor ? 'error' : ''}`}
              placeholder="Search tutor..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowTutorDropdown(true);
              }}
              onFocus={() => setShowTutorDropdown(true)}
              disabled={isEdit} // Don't allow changing tutor when editing
            />
            {showTutorDropdown && !isEdit && (
              <div className="tutor-dropdown">
                {getFilteredTutors().length === 0 ? (
                  <div className="no-results">No tutors found</div>
                ) : (
                  getFilteredTutors().map(tutor => (
                    <div
                      key={tutor._id}
                      className="tutor-option"
                      onClick={() => handleTutorSelect(tutor)}
                    >
                      <div className="tutor-name">{tutor.name}</div>
                      {tutor.email && (
                        <div className="tutor-email">{tutor.email}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {errors.tutor && <span className="error-message">{errors.tutor}</span>}
        </div>

        {/* Month and Year */}
        <div className="form-row">
          <div className="form-group">
            <label>Month *</label>
            <select
              value={formData.month}
              onChange={(e) => handleChange('month', parseInt(e.target.value))}
              className={errors.month ? 'error' : ''}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            {errors.month && <span className="error-message">{errors.month}</span>}
          </div>

          <div className="form-group">
            <label>Year *</label>
            <select
              value={formData.year}
              onChange={(e) => handleChange('year', parseInt(e.target.value))}
              className={errors.year ? 'error' : ''}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            {errors.year && <span className="error-message">{errors.year}</span>}
          </div>
        </div>

        {/* Amount */}
        <div className="form-group">
          <label>Amount ($) *</label>
          <input
            type="number"
            step="0.01"
            placeholder="Enter amount (use negative for deductions)"
            value={formData.amount}
            onChange={(e) => handleChange('amount', e.target.value)}
            className={errors.amount ? 'error' : ''}
          />
          <small className="help-text">
            Enter positive values for additions, negative values for deductions
          </small>
          {errors.amount && <span className="error-message">{errors.amount}</span>}
        </div>

        {/* Type */}
        <div className="form-group">
          <label>Type *</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange('type', e.target.value)}
          >
            {adjustmentTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Reason */}
        <div className="form-group">
          <label>Reason *</label>
          <textarea
            placeholder="Enter reason for adjustment"
            value={formData.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
            rows={4}
            className={errors.reason ? 'error' : ''}
          />
          <small className="help-text">
            {formData.reason.length}/10 characters minimum
          </small>
          {errors.reason && <span className="error-message">{errors.reason}</span>}
        </div>

        {/* Profit Share Impact Toggle */}
        <div className="form-group profit-share-toggle">
          <div className="profit-share-toggle-row">
            <div className="profit-share-toggle-text">
              <span className="profit-share-toggle-label">Affects other profit sharers</span>
              <small className="help-text">
                {formData.isProfitShareAdjustment
                  ? 'Personal — other profit sharers will NOT be affected'
                  : 'Cost shared with other profit sharers proportionally (default)'}
              </small>
            </div>
            <label className="paf-toggle-switch">
              <input
                type="checkbox"
                checked={!formData.isProfitShareAdjustment}
                onChange={(e) => handleChange('isProfitShareAdjustment', !e.target.checked)}
              />
              <span className="paf-toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="button" 
            className="btn-cancel" 
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-submit"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (isEdit ? 'Update Adjustment' : 'Add Adjustment')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentAdjustmentForm;
